import classNames from "classnames";
import { useState } from "react";
import { Tabs } from "..";
import { GuestCard } from "../../../../components/cards/guestCard";
import { NumberCard } from "../../../../components/cards/numberCard";
import { Icon } from "../../../../components/icon/icon";
import { ManageCoOrganizerModal } from "../../../../components/modal/app_modals/manageCoOrganizerModal";
import { Pill } from "../../../../components/pill";
import { ProgressBar } from "../../../../components/progressBar";
import { TableComponent } from "../../../../components/table";
import { GuestsTable } from "../../../../components/table/app_tables/guestsTable";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  CoOrganizerRole,
  CoOrganizerStatus,
  CreatedEvent,
  GetEventGuestsResponse,
  GetEventTicketsResponse,
  SingleCoOrganizer,
} from "../../../../redux/features/event/event.types";
import { useGetPurchaseStatisticsQuery } from "../../../../redux/features/payment/payment.api";
import { PayoutCurrency } from "../../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../../redux/hooks";
import { coorganizerRolePillMap } from "../../../../utils/constants";
import {
  EventPermissions,
  TicketSalesBreakdown,
} from "../../../../utils/types";

export const EventOverview = ({
  event,
  setTab,
  ticketBreakdown,
  ticketsData,
  guests,
  fetchingGuests = false,
  hasPaidTickets = false,
  eventCurrency,
  refetchEventDetails,
  userEventPermissions,
}: {
  event: CreatedEvent;
  setTab: (tab: Tabs) => void;
  ticketBreakdown: TicketSalesBreakdown;
  ticketsData?: GetEventTicketsResponse;
  guests?: GetEventGuestsResponse;
  fetchingGuests?: boolean;
  hasPaidTickets?: boolean;
  eventCurrency: PayoutCurrency;
  refetchEventDetails: () => void;
  userEventPermissions: EventPermissions[];
}) => {
  const [selectedOrganizer, setSelectedOrganizer] =
    useState<SingleCoOrganizer>();

  const { profile } = useAppSelector((state) => state.auth);

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const { data: statistics, isFetching: fetchingStatistics } =
    useGetPurchaseStatisticsQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true, skip: !hasPaidTickets },
    );

  return (
    <>
      {hasPaidTickets && userEventPermissions.includes("viewSales") ? (
        <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
          <div className="flex items-center justify-between gap-4">
            <MiniHeader header="Sales Overview" />

            <button
              type="button"
              className="text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
              onClick={() => {
                setTab("tickets");
              }}
            >
              View All
            </button>
          </div>

          <div className="grid grid-cols-1 gap-[10px] md:grid-cols-3">
            <NumberCard
              value={statistics?.data.totalTicketsSold ?? 0}
              text="Total Tickets Sold"
              isCurrency={false}
              tooltipId={"totalTicketsSold"}
              tooltipText={"The total number of tickets that have been bought."}
              loading={fetchingStatistics}
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalSalesRevenue ?? 0}
              text="Total Sales Revenue"
              tooltipId={"totalSalesRevenue"}
              tooltipText={
                "The total amount of tickets sold which includes our fees."
              }
              loading={fetchingStatistics}
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalAmountMade ?? 0}
              text="Total Amount Made"
              tooltipId={"totalAmountMade"}
              tooltipText={
                "You'll receive this amount in your payout account—your earnings from ticket sales after our fees have been deducted."
              }
              loading={fetchingStatistics}
              currency={eventCurrency}
            />
          </div>
        </BgWrapper>
      ) : undefined}

      {userEventPermissions.includes("viewGuestList") ? (
        <BgWrapper
          className={classNames("flex flex-col gap-3", {
            "!rounded-t-none": !hasPaidTickets,
          })}
        >
          <MiniHeader header="Guests" />

          <p className="text-2xl font-medium tracking-[-0.04em] text-grey-600">
            {guests?.data?.totalCount ?? 0}{" "}
            {(guests?.data?.totalCount ?? 0) === 1 ? "guest" : "guests"}
          </p>

          <ProgressBar
            step={ticketBreakdown.map((tb) => tb.purchaseQuantity)}
            className="!h-[8px]"
            colors={ticketBreakdown.map((tb) => tb.color.split(",")[0])}
          />

          <div className="mt-3 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <MiniHeader header="Recent guests" />

              <button
                type="button"
                className="text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
                onClick={() => {
                  setTab("guests");
                }}
              >
                View All
              </button>
            </div>

            <GuestsTable
              guests={guests?.data?.data?.slice(0, 5) ?? []}
              loading={fetchingGuests}
              showTimeAgo={false}
              ticketColor={ticketBreakdown.map((tb) => {
                return {
                  ticketId: tb.ticketId,
                  color: tb.color,
                };
              })}
              eventCurrency={eventCurrency}
              userEventPermissions={userEventPermissions}
            />
          </div>
        </BgWrapper>
      ) : undefined}

      {userEventPermissions.includes("viewCoorganizers") && (
        <BgWrapper className={classNames("flex flex-col gap-3")}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <MiniHeader
                header="Organizers"
                subheader="Add or manage a co-organizers or event manager"
              />
            </div>

            {userEventPermissions.includes("addCoorganizers") && (
              <button
                type="button"
                className="min-w-fit text-left text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
                onClick={() => {
                  handleModalOpen("manageCoOrganizerModal");
                }}
              >
                Add organizer
              </button>
            )}
          </div>

          <TableComponent
            headers={[]}
            rows={
              [
                {
                  email: `${event?.createdBy?.email}`,
                  userId: event.createdBy!,
                  role: "creator" as CoOrganizerRole,
                  status: "accepted" as CoOrganizerStatus,
                  invitedAt: `${event?.createdAt}`,
                },
                ...(event?.coOrganizers ?? []),
              ]?.map((organizer, idx) => {
                return {
                  id: idx,
                  items: [
                    <div
                      key={`organizer-row-${idx}`}
                      className="flex items-center gap-3"
                    >
                      <GuestCard
                        className="!w-fit break-all"
                        name={
                          organizer?.status === "accepted"
                            ? `${organizer?.userId?.firstName} ${organizer?.userId?.lastName}`
                            : "Pending Invite"
                        }
                        email={organizer.email}
                        timeAgo={undefined}
                        hideEmailOnMobile={false}
                        guest={undefined}
                        ticketColor={undefined}
                      />

                      <div className="flex-1">
                        <Pill
                          text={coorganizerRolePillMap[organizer.role].text}
                          variation={
                            coorganizerRolePillMap[organizer.role].variant
                          }
                        />
                      </div>

                      {profile?.email !== organizer.email &&
                      userEventPermissions.includes("manageCoorganizers") ? (
                        <button
                          className="w-fit"
                          type="button"
                          onClick={() => {
                            setSelectedOrganizer(organizer);
                            handleModalOpen("manageCoOrganizerModal");
                          }}
                        >
                          <Icon
                            svg="pencil"
                            width={20}
                            height={20}
                            className="text-grey-600"
                          />
                        </button>
                      ) : undefined}
                    </div>,
                  ],
                };
              }) ?? []
            }
            isLoading={false}
            isRowClickable={false}
            isTableEmpty={false}
            emptyTitle="No Co-Organizers"
            emptySubTitle=""
            emptyImage="/assets/images/guest-glass.png"
            isPaginated={false}
            currentPage={0}
            totalCount={0}
            onPageChange={(page) => {}}
          />

          <ManageCoOrganizerModal
            open={modal === "manageCoOrganizerModal" && modalOpen}
            handleClose={() => {
              setSelectedOrganizer(undefined);
              handleModalClose();
            }}
            organizer={selectedOrganizer}
            onSuccess={() => {
              setSelectedOrganizer(undefined);
              refetchEventDetails();
            }}
            eventId={event._id}
          />
        </BgWrapper>
      )}
    </>
  );
};
